<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">熱賣商品排行</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleExport">
          <i class="ri-download-line"></i>
          匯出 Excel
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>開始日期</label>
        <input
          v-model="filters.startDate"
          type="date"
          class="form-control"
          required
        />
      </div>
      <div class="filter-group">
        <label>結束日期</label>
        <input
          v-model="filters.endDate"
          type="date"
          class="form-control"
          required
        />
      </div>
      <div class="filter-group">
        <label>排序方式</label>
        <select v-model="filters.sortBy" class="form-control">
          <option value="quantity">銷售數量</option>
          <option value="revenue">銷售金額</option>
          <option value="growth">成長率</option>
        </select>
      </div>
      <div class="filter-group">
        <label>顯示數量</label>
        <input
          v-model.number="filters.limit"
          type="number"
          class="form-control"
          min="1"
          max="100"
        />
      </div>
      <button class="btn btn-primary" @click="fetchData">查詢</button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="dashboard-content">
      <!-- 圖表區 -->
      <div class="chart-section">
        <div class="chart-card">
          <BarChart
            v-if="products.length > 0"
            title="熱賣商品 Top 20"
            :data="chartData"
            xAxisKey="name"
            yAxisKey="value"
            :horizontal="true"
            height="500px"
            color="#4facfe"
          />
        </div>
      </div>

      <!-- 表格區 -->
      <div class="table-section">
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>排名</th>
                <th>商品名稱</th>
                <th>商品 SKU</th>
                <th>銷售數量</th>
                <th>銷售金額</th>
                <th>訂單數</th>
                <th>平均單價</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="products.length === 0">
                <td colspan="7" class="empty-state">沒有找到資料</td>
              </tr>
              <tr v-for="product in products" :key="product.productId">
                <td>{{ product.rank }}</td>
                <td>{{ product.productName || '-' }}</td>
                <td>{{ product.productSku || '-' }}</td>
                <td>{{ product.totalQuantity }}</td>
                <td>{{ formatCurrency(product.totalRevenue) }}</td>
                <td>{{ product.orderCount }}</td>
                <td>{{ formatCurrency(product.averagePrice) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { reportsService } from '@/api/modules/reports.js';
import { showError, showSuccess } from '@/utils/swal.js';
import BarChart from '@/components/charts/BarChart.vue';

export default {
  name: 'ProductRanking',
  components: {
    BarChart
  },
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const products = ref([]);
    const filters = ref({
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      sortBy: 'quantity',
      limit: 20
    });

    const chartData = computed(() => {
      return products.value.slice(0, 20).map(item => ({
        name: item.productName || item.productSku || '未知商品',
        value: filters.value.sortBy === 'revenue' ? item.totalRevenue : item.totalQuantity
      }));
    });

    const fetchData = async () => {
      if (!filters.value.startDate || !filters.value.endDate) {
        showError('錯誤', '請選擇開始日期和結束日期');
        return;
      }

      try {
        loading.value = true;
        error.value = null;
        const response = await reportsService.getProductRanking(filters.value);
        if (response.success) {
          products.value = response.data.products || [];
        } else {
          error.value = response.message || '取得資料失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得資料失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleExport = async () => {
      if (!filters.value.startDate || !filters.value.endDate) {
        showError('錯誤', '請先選擇日期範圍');
        return;
      }

      try {
        loading.value = true;
        const params = {
          reportType: 'products',
          format: 'excel',
          ...filters.value
        };
        const blob = await reportsService.exportReport(params);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `熱賣商品排行_${filters.value.startDate}_${filters.value.endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('成功', '報表已匯出');
      } catch (err) {
        showError('匯出失敗', err.response?.data?.message || err.message || '匯出失敗');
      } finally {
        loading.value = false;
      }
    };

    const formatCurrency = (value) => {
      if (!value) return '$0';
      return `$${parseInt(value).toLocaleString()}`;
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      products,
      filters,
      chartData,
      fetchData,
      handleExport,
      formatCurrency
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.chart-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chart-card {
  width: 100%;
}

.table-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
